import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { computePHash, compressImage } from '../lib/phash.js';
import { todayStr, fmtDate, fmtNum } from '../lib/util.js';
import CharacterSelect, { charById } from './CharacterSelect.jsx';

const STATUS_LABEL = {
  provisional: { text: 'บันทึกแล้ว', cls: 'ok' },
  confirmed: { text: 'ยืนยันแล้ว', cls: 'ok' },
  rejected: { text: 'ไม่ผ่าน', cls: 'bad' },
};

export default function Home({ auth, initialState, char, onCharChange }) {
  const [state, setState] = useState(initialState);
  const [steps, setSteps] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [celebrate, setCelebrate] = useState(null);
  const [showChar, setShowChar] = useState(false);

  const { employee, config, todayMission, todayActivity, recent, tickets } = state;
  const goal = todayMission?.target_steps || config.daily_step_goal;
  const doneSteps = todayActivity?.steps || 0;
  const pct = Math.min(100, Math.round((doneSteps / goal) * 100));

  function onPick(e) {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(f ? URL.createObjectURL(f) : '');
  }

  async function refresh() {
    const s = await api('getState', { ...auth, activityDate: todayStr() });
    setState(s);
  }

  // ดึงสถานะล่าสุดทุกครั้งที่เปิดหน้านี้ (กันสถานะหายเมื่อสลับแท็บ)
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit() {
    setErr('');
    const n = parseInt(steps, 10);
    if (!Number.isFinite(n) || n <= 0) { setErr('กรอกจำนวนก้าวก่อนนะ'); return; }
    setBusy(true);
    try {
      let phash, screenshotBase64;
      if (file) {
        [phash, screenshotBase64] = await Promise.all([computePHash(file), compressImage(file)]);
      }
      const res = await api('submitSteps', {
        ...auth, steps: n, activityDate: todayStr(), phash, screenshotBase64,
      });
      await refresh();
      setSteps(''); setFile(null); setPreview('');
      setCelebrate({ xp: res.xpGained || 0, goal: res.goalReached, tickets: res.ticketsGained || 0 });
      setTimeout(() => setCelebrate(null), 2600);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="app">
      {/* header */}
      <header className="topbar">
        <div>
          <p className="hi">สวัสดี {employee.display_name} 👋</p>
          {employee.branch && <p className="branch">สาขา {employee.branch}</p>}
        </div>
        <div className="stats">
          <div className="badge-chip"><img src="/assets/ui/coin.png" alt="" /><b>{fmtNum(employee.total_xp)}</b><span>XP</span></div>
          <div className="badge-chip"><img src="/assets/ui/portrait.png" alt="" /><b>{employee.current_streak}</b><span>สตรีค</span></div>
          <div className="badge-chip"><img src="/assets/ui/chest.png" alt="" /><b>{fmtNum(tickets || 0)}</b><span>ตั๋ว</span></div>
        </div>
      </header>

      {employee.freeze_tokens > 0 && (
        <p className="freeze-note">❄️ มีวันพัก {employee.freeze_tokens} วัน — ขาดได้โดยสตรีคไม่หลุด</p>
      )}

      <button className="char-btn" onClick={() => setShowChar(true)}>
        <img src={charById(char).src} alt="" />
        <span>ตัวเดินของฉัน: <b>{charById(char).name}</b></span>
        <em>เปลี่ยน ›</em>
      </button>

      {/* mission */}
      <section className="card mission">
        <div className="mission-head">
          <h2>{todayMission?.title || 'เดินให้ครบเป้าวันนี้'}</h2>
          <span className="target">{fmtNum(goal)} ก้าว</span>
        </div>
        <div className="bar"><div className="bar-fill" style={{ width: `${pct}%` }} /></div>
        <p className="bar-label">
          {doneSteps ? `${fmtNum(doneSteps)} ก้าววันนี้ · ${pct}%` : 'ยังไม่ได้ส่งวันนี้'}
        </p>
      </section>

      {/* submit */}
      <section className="card">
        <h3 className="card-title">ส่งก้าววันนี้</h3>
        <label className="field">
          <span>จำนวนก้าว</span>
          <input
            type="number" inputMode="numeric" placeholder="เช่น 6500"
            value={steps} onChange={(e) => setSteps(e.target.value)}
          />
        </label>

        <label className="uploader">
          {preview ? <img src={preview} alt="ตัวอย่าง" /> : <span>📷 แนบรูปหน้าจอนับก้าว</span>}
          <input type="file" accept="image/*" capture="environment" onChange={onPick} hidden />
        </label>

        {err && <p className="err">{err}</p>}
        <button className="btn" onClick={submit} disabled={busy}>
          {busy ? 'กำลังส่ง…' : todayActivity ? 'อัปเดตก้าววันนี้' : 'ส่งเลย!'}
        </button>

        {todayActivity && (
          <p className="today-note">
            วันนี้ส่งแล้ว {fmtNum(todayActivity.steps)} ก้าว · +{todayActivity.xp_awarded} XP
            {' '}
            <StatusBadge status={todayActivity.status} />
          </p>
        )}
      </section>

      {/* history */}
      {recent?.length > 0 && (
        <section className="card">
          <h3 className="card-title">ประวัติ</h3>
          <ul className="history">
            {recent.map((a) => (
              <li key={a.id}>
                <span className="h-date">{fmtDate(a.activity_date)}</span>
                <span className="h-steps">{fmtNum(a.steps)} ก้าว</span>
                <span className="h-xp">+{a.xp_awarded}</span>
                <StatusBadge status={a.status} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="foot">Nose Tea Move · ขยับทุกวัน สนุกทุกก้าว 🌿</footer>

      {celebrate && (
        <div className="celebrate" onClick={() => setCelebrate(null)}>
          <div className="celebrate-card">
            <div className="sparkles"><i /><i /><i /><i /><i /><i /></div>
            <div className="nong">🥳</div>
            {celebrate.xp > 0
              ? <p className="c-xp">+{celebrate.xp} XP!</p>
              : <p className="c-xp small">อัปเดตแล้ว</p>}
            {celebrate.goal && <p className="c-goal">ครบเป้าวันนี้ เก่งมาก! 🎉</p>}
            {celebrate.tickets > 0 && <p className="c-goal">🎟️ ได้ตั๋วจับฉลาก +{celebrate.tickets}</p>}
          </div>
        </div>
      )}

      {showChar && (
        <CharacterSelect auth={auth} current={char}
          onClose={() => setShowChar(false)} onSelect={onCharChange} />
      )}
    </main>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { text: status, cls: '' };
  return <span className={`sbadge ${s.cls}`}>{s.text}</span>;
}
